<template>
  <div>
    <AddBeneficialOwnerModal
      :open="isAddBeneficialOwnerModalOpen"
      @close-modal="isAddBeneficialOwnerModalOpen = false"
    />
    <PaperCard :has_header="false">
      <template #body>
        <div class="mb-8">
          <p class="font-inter text-lg font-semibold mb-2 text-strongest">Beneficial Owners</p>
          <AsteriskNotice
            size="small_medium"
            class="!text-xs lg:text-sm"
            text="At least one beneficial owner is required to be listed with the investment."
          />
          <AsteriskNotice
            size="small_medium"
            class="!text-xs lg:text-sm"
            text="A beneficial owner is an individual who owns 20% or more of the entity or trust."
          />
        </div>

        <div class="flex flex-col-reverse lg:flex-col">
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <!-- Self option -->
            <div
              @click="toggleSelf"
              class="cursor-pointer rounded-xl p-4 flex items-center gap-5 h-[56px]"
              :class="isSelfSelected ? 'bg-radio-checkbox-selector' : ''"
            >
              <div
                class="h-[18px] w-[18px] rounded flex items-center justify-center border"
                :class="
                  isSelfSelected
                    ? 'bg-radio-checkbox-checked border-transparent'
                    : 'bg-transparent border-radio-checkbox-outline'
                "
              >
                <Icon
                  v-if="isSelfSelected"
                  name="Check"
                  class="text-container-bg h-3 w-3"
                  :stroke-width="2.5"
                />
              </div>
              <p
                class="font-inter whitespace-nowrap text-strongest"
                :class="isSelfSelected ? 'font-semibold' : 'font-medium'"
              >
                I am the beneficial owner
              </p>
            </div>

            <!-- Contact options -->
            <div
              v-for="contact in contactOptions"
              :key="contact.record_id"
              @click="toggleContact(contact)"
              class="cursor-pointer rounded-xl p-4 flex items-center gap-5 h-[56px]"
              :class="isSelected(contact) ? 'bg-radio-checkbox-selector' : ''"
            >
              <div
                class="h-[18px] w-[18px] rounded flex items-center justify-center border"
                :class="
                  isSelected(contact)
                    ? 'bg-radio-checkbox-checked border-transparent'
                    : 'bg-transparent border-radio-checkbox-outline'
                "
              >
                <Icon
                  v-if="isSelected(contact)"
                  name="Check"
                  class="text-container-bg h-3 w-3"
                  :stroke-width="2.5"
                />
              </div>
              <p
                class="font-inter whitespace-nowrap text-strongest"
                :class="isSelected(contact) ? 'font-semibold' : 'font-medium'"
              >
                {{ contact.first_name }} {{ contact.last_name }}
              </p>
            </div>
          </div>

          <Button
            @click="isAddBeneficialOwnerModalOpen = true"
            variant="paper-primary"
            class="mb-6 lg:mb-0 lg:mt-4 w-fit"
          >
            <template #label>Add New</template>
            <template #icon>
              <Icon name="Plus" class="h-4 w-4 text-strongest" :stroke-width="2.5" />
            </template>
          </Button>
        </div>
      </template>
    </PaperCard>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import { PaperCard, AsteriskNotice, Icon, Button } from "@/components";
import { usePurchaseFlowStore, useDataStore } from "@/stores";
import AddBeneficialOwnerModal from "./AddBeneficialOwnerModal.vue";

const purchaseFlowStore = usePurchaseFlowStore();
const dataStore = useDataStore();

const isAddBeneficialOwnerModalOpen = ref(false);

const entityId = computed(() => purchaseFlowStore.active_entity_account.record_id);

const isSelfSelected = computed(() => dataStore.contact.entity_ids?.includes(entityId.value));

const contactOptions = computed(() => {
  const allContacts = [
    ...(dataStore.joint_contacts || []),
    ...(dataStore.beneficial_owner_contacts || []),
  ];

  const seen = new Set();
  return allContacts.filter((c) => {
    if (seen.has(c.record_id)) return false;
    seen.add(c.record_id);
    return true;
  });
});

function isSelected(contact) {
  return contact.entity_ids?.includes(entityId.value);
}

function toggleSelf() {
  const ids = dataStore.contact.entity_ids || [];
  if (ids.includes(entityId.value)) {
    dataStore.contact.entity_ids = ids.filter((id) => id !== entityId.value);
  } else {
    dataStore.contact.entity_ids = [...ids, entityId.value];
  }
}

function toggleContact(contact) {
  const ids = contact.entity_ids || [];
  if (ids.includes(entityId.value)) {
    contact.entity_ids = ids.filter((id) => id !== entityId.value);
  } else {
    contact.entity_ids = [...ids, entityId.value];
  }
}
</script>
